import React from 'react';
import {
  AgnesDataTable,
  AgnesPageHeader,
  AgnesSwitch,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTooltip,
} from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Download,
  HelpCircle,
  Info,
  LineChart,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import AfastamentoFilter from './AfastamentoFilter';
import { SearchInput } from '@/components/SearchInput';
import { CardTitle } from '@/components/ui/SectionCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { type LinhaAfastamento, useAfastamentoStore } from '@/stores/useAfastamentoStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { type Barra, COR } from '@/lib/afastamento';
import { countFiltros } from '@/lib/filtros';
import UtilsMask from '@/utils/UtilsMask';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

const fmtPct = (v: number) => `${v.toFixed(2)}%`;
const CAMPO_SEM_PREENCHIMENTO = 'Campo sem preenchimento no XML';

function CampoAfastamento({
  valor,
  className,
  dataQa,
}: {
  valor: string | null | undefined;
  className?: string;
  dataQa: string;
}) {
  if (valor) {
    return (
      <span className={className} title={valor}>
        {valor}
      </span>
    );
  }

  return (
    <AgnesTooltip content={CAMPO_SEM_PREENCHIMENTO}>
      <span data-qa={dataQa} className="flex items-center gap-1.5 text-agnes-error">
        <CircleAlert className="h-4 w-4 shrink-0" /> {CAMPO_SEM_PREENCHIMENTO}
      </span>
    </AgnesTooltip>
  );
}

// barra horizontal com label + % (tabs Categoria/Motivos)
function BarraHorizontal({ label, count, valor, cor = COR.Afastamento }: Barra) {
  return (
    <div className="py-2">
      <p className="text-sm text-agnes-text">
        {label} ({count})
      </p>
      <div className="mt-1 flex items-center gap-3">
        <span className="w-16 shrink-0 text-sm font-medium" style={{ color: cor }}>
          {fmtPct(valor)}
        </span>
        <div className="h-3 flex-1 overflow-hidden rounded-full bg-agnes-bg-muted">
          <div className="h-full rounded-full" style={{ width: `${valor}%`, background: cor }} />
        </div>
      </div>
    </div>
  );
}

export default function PageTrabalhadorAfastamento() {
  const {
    vm,
    lista,
    total,
    page,
    size,
    sortBy,
    sortOrder,
    afastados,
    inativos,
    descanso,
    semMotivo,
    busca,
    filtros,
    opcoes,
    carregandoLista,
    exportando,
    carregarDashboard,
    carregarHistorico,
    carregarFiltros,
    aplicarFiltros,
    setToggle,
    setBusca,
    buscar,
    setPage,
    setSize,
    setSort,
    exportar,
  } = useAfastamentoStore();

  const [showCharts, setShowCharts] = React.useState(true);
  const [tab, setTab] = React.useState('total');
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences('trabalhador-afastamento');

  React.useEffect(() => {
    carregarFiltros();
    carregarDashboard();
    carregarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // donut: 4 segmentos da visão geral
  const conic = (() => {
    let acc = 0;
    const stops = vm.visaoGeral
      .filter((s) => s.valor > 0)
      .map((s) => {
        const from = acc;
        acc += s.valor;
        return `${s.cor} ${from}% ${acc}%`;
      });
    return stops.length
      ? `conic-gradient(${stops.join(', ')})`
      : `conic-gradient(${COR['Motivo não informado']} 0% 100%)`;
  })();

  const columns = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r: LinhaAfastamento) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empresa',
      width: 280,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.empresa}</p>
          <p className="text-agnes-text-muted">{UtilsMask.cnpj(r.cnpj)}</p>
        </div>
      ),
    },
    {
      key: 'cpf',
      header: 'CPF',
      width: 140,
      sortable: true,
      render: (r: LinhaAfastamento) => UtilsMask.cpf(r.cpf),
    },
    {
      key: 'matricula',
      header: 'Matrícula',
      width: 150,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <span className="block max-w-[130px] truncate" title={r.matricula}>
          {r.matricula}
        </span>
      ),
    },
    {
      key: 'motivo',
      header: 'Motivo de afastamento',
      width: 220,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.motivo}
          className="block max-w-[200px] truncate"
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-motivo`}
        />
      ),
    },
    {
      key: 'inicio',
      header: 'Início do afastamento',
      width: 160,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.inicio}
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-inicio`}
        />
      ),
    },
    {
      key: 'fim',
      header: 'Fim do afastamento',
      width: 200,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.fim}
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-fim`}
        />
      ),
    },
    {
      key: 'grupoCategoria',
      header: 'Grupo de categoria',
      width: 180,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.grupoCategoria}
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-grupoCategoria`}
        />
      ),
    },
    {
      key: 'categoria',
      header: 'Categoria',
      width: 220,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.categoria}
          className="block max-w-[200px] truncate"
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-categoria`}
        />
      ),
    },
    {
      key: 'regime',
      header: 'Regime Previdenciário',
      width: 180,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.regime}
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-regime`}
        />
      ),
    },
    {
      key: 'cbo',
      header: 'CBO',
      width: 260,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.cbo}
          className="block max-w-[240px] truncate"
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-cbo`}
        />
      ),
    },
    {
      key: 'cargo',
      header: 'Cargo',
      width: 200,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.cargo}
          className="block max-w-[180px] truncate"
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-cargo`}
        />
      ),
    },
    {
      key: 'situacao',
      header: 'Situação',
      width: 120,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.situacao}
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-situacao`}
        />
      ),
    },
    {
      key: 'idDocumento',
      header: 'ID do evento',
      width: 320,
      sortable: true,
      render: (r: LinhaAfastamento) => (
        <CampoAfastamento
          valor={r.idDocumento}
          className="block max-w-[300px] truncate"
          dataQa={`afastamento-campo-sem-preenchimento-${r._key}-idDocumento`}
        />
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<CalendarClock />}
        title="Afastamento e inatividade"
        subtitle="Monitore as informações relativas aos eventos S-2230 - Afastamento Temporário do eSocial"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Trabalhador' },
          { label: 'Afastamento e inatividade' },
        ]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface p-4">
        {/* barra de switches + filtro */}
        <div className="flex flex-wrap items-center justify-end gap-6">
          <AgnesSwitch
            label="Afastados"
            checked={afastados}
            onCheckedChange={(v) => setToggle('afastados', v)}
          />
          <AgnesSwitch
            label="Inativos"
            checked={inativos}
            onCheckedChange={(v) => setToggle('inativos', v)}
          />
          <AgnesSwitch
            label="Descanso remunerado"
            checked={descanso}
            onCheckedChange={(v) => setToggle('descanso', v)}
          />
          <AfastamentoFilter
            opcoes={opcoes}
            onApply={aplicarFiltros}
            count={countFiltros(filtros, { dateRanges: [['startDate', 'endDate']] })}
          />
        </div>

        {/* card de gráficos colapsável */}
        <div className="mt-4 rounded-xl border border-agnes-border bg-agnes-bg-default shadow-agnes-subtle">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2 text-agnes-primary">
              <LineChart className="h-5 w-5" />
              <span className="font-medium">Gráficos por categoria e motivo de afastamento</span>
            </div>
            <div className="flex items-center gap-2 text-agnes-primary">
              {!semMotivo && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium"
                  onClick={() => setShowCharts((s) => !s)}
                >
                  {showCharts ? 'Ocultar gráficos' : 'Exibir gráficos'}
                  {showCharts ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
              )}
              <AgnesTooltip
                className="max-w-xs whitespace-normal"
                content="Os gráficos consideram os dados totais e não são afetados pelos filtros, exceto se selecionar 'Motivo não informado' no botão junto à tabela. Neste caso, os gráficos serão ocultados."
              >
                <HelpCircle className="h-4 w-4 text-agnes-text-muted" />
              </AgnesTooltip>
            </div>
          </div>

          {showCharts && !semMotivo && (
            <AgnesTabs value={tab} onValueChange={setTab} className="border-t border-agnes-border">
              <AgnesTabsList className="px-4">
                <AgnesTabsTrigger value="total">Total de afastamentos</AgnesTabsTrigger>
                <AgnesTabsTrigger value="categoria">Afastamentos por categoria</AgnesTabsTrigger>
                <AgnesTabsTrigger value="motivos">Motivos de afastamento</AgnesTabsTrigger>
              </AgnesTabsList>

              {/* Tab 1 — Total */}
              <AgnesTabsContent value="total" className="p-4">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {/* Visão geral — donut */}
                  <div className="rounded-xl border border-agnes-border bg-agnes-bg-default p-4 shadow-agnes-card">
                    <div className="mb-4 flex items-center justify-between">
                      <CardTitle>Visão geral</CardTitle>
                      <AgnesTooltip
                        className="max-w-xs whitespace-normal"
                        content="O gráfico de visão geral considera todos os registros, informando a distribuição entre afastamento, inatividade, descanso remunerado e motivo não informado."
                      >
                        <Info className="h-4 w-4 text-agnes-primary" />
                      </AgnesTooltip>
                    </div>
                    <div className="flex items-center gap-6">
                      <div
                        className="relative h-40 w-40 shrink-0 rounded-full"
                        style={{ background: conic }}
                      >
                        <div className="absolute inset-[22%] rounded-full bg-agnes-bg-default" />
                      </div>
                      <ul className="space-y-2 text-sm">
                        {vm.visaoGeral.map((s) => (
                          <li key={s.label} className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ background: s.cor }}
                            />
                            <span className="text-agnes-text">
                              {s.label} {fmtPct(s.valor)} (<b>{s.count}</b>/{vm.total})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* barras de situação */}
                  <div className="space-y-3">
                    <div className="rounded-xl border border-agnes-border bg-agnes-bg-default p-4 shadow-agnes-card">
                      <div className="mb-2 flex items-center justify-between">
                        <CardTitle>
                          Motivo informado - Registros por situação ({vm.total})
                        </CardTitle>
                        <AgnesTooltip
                          className="max-w-xs whitespace-normal"
                          content="O gráfico de registros por situação segrega apenas os eventos que possuem motivo de afastamento preenchido no xml e informa a quantidade e a proporção (%) dos casos por situação (afastamento, inatividade e descanso remunerado)."
                        >
                          <Info className="h-4 w-4 text-agnes-primary" />
                        </AgnesTooltip>
                      </div>
                      <div className="flex h-3 overflow-hidden rounded-full bg-agnes-bg-muted">
                        {vm.situacao
                          .filter((s) => s.valor > 0)
                          .map((s) => (
                            <div
                              key={s.label}
                              style={{ width: `${s.valor}%`, background: s.cor }}
                            />
                          ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        {vm.situacao.map((s) => (
                          <span key={s.label} className="flex items-center gap-1.5">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ background: s.cor }}
                            />
                            {s.label} ({s.count}){' '}
                            <span className="font-medium" style={{ color: s.cor }}>
                              {fmtPct(s.valor)}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-agnes-border bg-agnes-bg-default p-4 shadow-agnes-card">
                      <div className="mb-2 flex items-center justify-between">
                        <CardTitle>Motivo informado ({vm.informado.count})</CardTitle>
                        <AgnesTooltip
                          className="max-w-xs whitespace-normal"
                          content="O contador relaciona a quantidade de S-2230 para os quais o campo de motivo de afastamento foi preenchido."
                        >
                          <Info className="h-4 w-4 text-agnes-primary" />
                        </AgnesTooltip>
                      </div>
                      <div className="h-3 overflow-hidden rounded-full bg-agnes-bg-muted">
                        <div
                          className="h-full rounded-full bg-agnes-primary"
                          style={{ width: `${vm.informado.valor}%` }}
                        />
                      </div>
                      <p className="mt-1 text-right text-sm text-agnes-text-muted">
                        {fmtPct(vm.informado.valor)} do total de registros ({vm.total})
                      </p>
                    </div>

                    <div className="rounded-xl border border-agnes-border bg-agnes-bg-default p-4 shadow-agnes-card">
                      <div className="mb-2 flex items-center justify-between">
                        <CardTitle>
                          Motivo não informado ou arquivo de retorno ({vm.naoInformado.count})
                        </CardTitle>
                        <AgnesTooltip
                          className="max-w-xs whitespace-normal"
                          content="O contador relaciona a quantidade de S-2230 para os quais o campo de motivo de afastamento não foi preenchido ou arquivos de retorno de afastamento, onde apenas consta a informação da data de retorno dispensando o preenchimento dos demais campos."
                        >
                          <Info className="h-4 w-4 text-agnes-primary" />
                        </AgnesTooltip>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: COR['Motivo não informado'] }}
                        />
                        <span className="ml-auto text-sm text-agnes-text-muted">
                          {fmtPct(vm.naoInformado.valor)}
                        </span>
                      </div>
                      <p className="mt-1 text-right text-sm text-agnes-text-muted">
                        {fmtPct(vm.naoInformado.valor)} do total de registros ({vm.total})
                      </p>
                    </div>
                  </div>
                </div>
              </AgnesTabsContent>

              {/* Tab 2 — Categoria */}
              <AgnesTabsContent value="categoria" className="p-4">
                {vm.categoria.length === 0 ? (
                  <EmptyState title="Sem dados de categoria" />
                ) : (
                  vm.categoria.map((c) => <BarraHorizontal key={c.label} {...c} />)
                )}
              </AgnesTabsContent>

              {/* Tab 3 — Motivos */}
              <AgnesTabsContent value="motivos" className="p-4">
                <div className="flex justify-end">
                  <AgnesTooltip
                    className="max-w-xs whitespace-normal"
                    content="O gráfico por motivo de afastamento segrega apenas os eventos cujo motivo foi informado e traz por código de motivo de afastamento a quantidade e a distribuição das ocorrências (%) de acordo com o motivo informado no evento S-2230 do eSocial."
                  >
                    <Info className="h-4 w-4 text-agnes-primary" />
                  </AgnesTooltip>
                </div>
                {vm.motivos.length === 0 ? (
                  <EmptyState title="Sem dados de motivo" />
                ) : (
                  vm.motivos.map((m) => <BarraHorizontal key={m.label} {...m} />)
                )}
              </AgnesTabsContent>
            </AgnesTabs>
          )}
        </div>

        {/* busca + switch + exportar */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="min-w-[280px] flex-1">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={buscar}
              placeholder="Pesquisar por CNPJ, matrícula, CPF ou motivo de afastamento"
            />
          </div>
          <AgnesSwitch
            label="Motivo não informado"
            labelPosition="right"
            checked={semMotivo}
            onCheckedChange={(v) => setToggle('semMotivo', v)}
          />
          <ColumnsMenu
            data-qa="afastamento-colunas"
            columns={columns}
            hiddenColumnKeys={hiddenColumnKeys}
            onToggleColumn={toggleColumn}
          />
          <ExportButton
            variant="outlined"
            startIcon={<Download className="h-4 w-4" />}
            disabled={exportando}
            onClick={exportar}
          >
            Exportar
          </ExportButton>
        </div>

        {/* tabela */}
        {!carregandoLista && lista.length === 0 ? (
          <EmptyState className="mt-4" title="Nenhum afastamento para exibir" />
        ) : (
          <TableWrapper className="mt-4">
            <AgnesDataTable<LinhaAfastamento>
              columns={columns}
              hiddenColumnKeys={hiddenColumnKeys}
              data={lista}
              rowKey={(r) => r._key}
              loading={carregandoLista}
              sort={{ sortBy, sortOrder, onSortChange: setSort }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: setPage,
                onPageSizeChange: setSize,
              }}
            />
          </TableWrapper>
        )}
      </div>
    </>
  );
}
